/**
 * ClientAvatar — Initials circle with iridescent gradient fill.
 *
 * Gradient presets cycle through brand colours; gradientIndex selects preset.
 * No i18n keys — initials are data-driven. Accessibility handled by parent row.
 */

import React, { useMemo } from 'react';
import { Text, StyleSheet } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';

const GRADIENT_START = { x: 0, y: 0 };
const GRADIENT_END = { x: 1, y: 1 };

const AVATAR_GRADIENTS: readonly (readonly [string, string])[] = [
  ['#EC4899', '#A78BFA'], // pink → lavender
  ['#14B8A6', '#A78BFA'], // teal → lavender
  ['#F59E0B', '#EC4899'], // amber → pink
  ['#6366F1', '#14B8A6'], // indigo → teal
  ['#10B981', '#A78BFA'], // green → lavender
] as const;

interface ClientAvatarProps {
  initials: string;
  gradientIndex?: number;
  size?: number;
}

export const ClientAvatar: React.FC<ClientAvatarProps> = React.memo(function ClientAvatar({
  initials,
  gradientIndex = 0,
  size = 36,
}) {
  const index = Math.abs(gradientIndex) % AVATAR_GRADIENTS.length;
  const colors = AVATAR_GRADIENTS[index] as [string, string];

  const sizeStyle = useMemo(
    () => ({ width: size, height: size, borderRadius: size / 2 }),
    [size]
  );

  return (
    <LinearGradient
      colors={colors}
      start={GRADIENT_START}
      end={GRADIENT_END}
      style={[styles.avatar, sizeStyle]}
      accessibilityElementsHidden={true}
    >
      <Text
        style={[styles.initials, { fontSize: Math.round(size * 0.33) }]}
        numberOfLines={1}
      >
        {initials.slice(0, 2).toUpperCase()}
      </Text>
    </LinearGradient>
  );
});

const styles = StyleSheet.create({
  avatar: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  initials: {
    color: '#FFFFFF',
    fontWeight: '700',
  },
});
